import logger from '../../utils/logger';
import React, { useState, useEffect } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { aiConsentService } from '../../services/aiConsentService';
import { aiConfigService } from '../../services/aiConfigService';
import { useAuth } from '../../context/AuthContext';
import AIConsentDialog from '../dialogs/AIConsentDialog';

/**
 * AI Toggle for Navbar
 *
 * Simple toggle switch to enable/disable AI features.
 * Shows consent dialog on first enable attempt.
 * Positioned in navbar between StatusBar and Search.
 *
 * States:
 * - OFF (gray): AI features disabled
 * - ON (blue): AI features enabled
 * - Loading: Checking/updating consent status
 */
const AIToggleNavbar = () => {
  const { user, setUser } = useAuth();
  const [aiEnabled, setAiEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hasDefaultAi, setHasDefaultAi] = useState(true);

  // Load AI consent status on mount
  useEffect(() => {
    loadConsentStatus();
  }, []);

  const loadConsentStatus = async () => {
    try {
      setLoading(true);
      setHasError(false);
      const status = await aiConsentService.getStatus();
      setAiEnabled(status.aiConsentGiven);
      if (status.aiConsentGiven) {
        try {
          const config = await aiConfigService.getDefault();
          setHasDefaultAi(!!config?.data?.connectionId);
        } catch (e) {
          setHasDefaultAi(false);
        }
      }
    } catch (error) {
      logger.error('Failed to load AI consent status:', error);
      setAiEnabled(false);
      setHasError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleClick = () => {
    // If there's an error, retry loading status
    if (hasError) {
      loadConsentStatus();
      return;
    }

    if (aiEnabled) {
      // User wants to disable AI - do it immediately without dialog
      handleDisableAI();
    } else {
      // User wants to enable AI - show consent dialog
      setShowConsentDialog(true);
    }
  };

  const handleAcceptConsent = async () => {
    try {
      setLoading(true);
      setShowConsentDialog(false);

      const result = await aiConsentService.enable();
      if (result.success) {
        setAiEnabled(true);
        // Update user context to reflect AI consent change
        if (user) {
          setUser({ ...user, aiConsentGiven: true });
        }
        // Check if user has a default AI connection configured
        try {
          const config = await aiConfigService.getDefault();
          setHasDefaultAi(!!config?.data?.connectionId);
        } catch (e) {
          setHasDefaultAi(false);
        }
      } else {
        logger.error('Failed to enable AI:', result.message);
        alert('Failed to enable AI features. Please try again.');
      }
    } catch (error) {
      logger.error('Error enabling AI:', error);
      alert('Failed to enable AI features. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineConsent = () => {
    setShowConsentDialog(false);
    // Leave AI disabled
  };

  const handleDisableAI = async () => {
    try {
      setLoading(true);
      const result = await aiConsentService.disable();
      if (result.success) {
        setAiEnabled(false);
        // Update user context to reflect AI consent change
        if (user) {
          setUser({ ...user, aiConsentGiven: false });
        }
      } else {
        logger.error('Failed to disable AI:', result.message);
        alert('Failed to disable AI features. Please try again.');
      }
    } catch (error) {
      logger.error('Error disabling AI:', error);
      alert('Failed to disable AI features. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <button
          onClick={handleToggleClick}
          disabled={loading || hasError}
          className={`
            relative inline-flex h-6 w-11 items-center rounded-full transition-colors border
            focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900
            ${loading || hasError ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
            ${aiEnabled
              ? 'bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500'
              : hasError
              ? 'bg-red-400 dark:bg-red-600 border-red-400 dark:border-red-600'
              : 'bg-gray-300 dark:bg-gray-600 border-gray-400 dark:border-gray-500'
            }
          `}
          title={
            hasError
              ? 'Failed to load AI status - Click to retry'
              : aiEnabled
              ? 'AI features enabled - Click to disable'
              : 'AI features disabled - Click to enable'
          }
        >
          <span
            className={`
              inline-flex h-4 w-4 items-center justify-center transform rounded-full bg-white transition-transform
              ${aiEnabled ? 'translate-x-6' : 'translate-x-1'}
            `}
          >
            {loading && (
              <Loader2 size={10} className="text-gray-600 animate-spin" />
            )}
          </span>
        </button>

        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
          AI
        </span>
        {aiEnabled && !hasDefaultAi && (
          <span className="text-[10px] text-amber-600 dark:text-amber-400 whitespace-nowrap">
            Configure in Connections
          </span>
        )}
      </div>

      {/* Consent Dialog */}
      <AIConsentDialog
        isOpen={showConsentDialog}
        onAccept={handleAcceptConsent}
        onDecline={handleDeclineConsent}
      />
    </>
  );
};

export default AIToggleNavbar;
